import { useRef } from "react";
import { Link } from "react-router-dom";
import { useSectionScrollProgress } from "@/hooks/useSharedScroll";

/**
 * Services — split-screen scroll section.
 *
 * Layout:
 *   LEFT  : full-bleed image panel (fills the entire left half of the viewport).
 *   RIGHT : sticky info column — number, title, kicker, guide, progress bars.
 *
 * Perf model (v2, unified with WhatIsTherapy/Manifesto/HowWeWork):
 *  - NO continuous rAF loop, NO JS easing/smoothing.
 *  - A single-flight rAF throttle applied ONCE per scroll frame.
 *  - Progress applied 1:1 to visuals → perfect scroll sync, zero drift.
 *  - Diff-cache prevents redundant DOM writes.
 *  - Never touches React state → zero re-renders on scroll.
 */

type Service = {
  id: string;
  number: string;
  title: string;
  kicker: string;
  guide: string;
  image: string;
  tint: string; // OKLCH triplet: "L C H"
};

const SERVICES: Service[] = [
  {
    id: "individual",
    number: "01",
    title: "Individual Therapy",
    kicker: "One person, one quiet room.",
    guide: "For you, if lately you've been carrying too much on your own.",
    image:
      "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/62c760080221779d084a0ca3813ab0e5.png",
    tint: "0.905 0.045 55",
  },
  {
    id: "couples",
    number: "02",
    title: "Couples Therapy",
    kicker: "Two voices, learning to listen.",
    guide: "For the two of you, when conversation keeps turning into conflict.",
    image:
      "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/920b156d9ce0f4fffe7a594d52853923.png",
    tint: "0.895 0.05 25",
  },
  {
    id: "online",
    number: "03",
    title: "Online Sessions",
    kicker: "Wherever feels safest to you.",
    guide: "For you, if the idea of walking into a room feels like too much.",
    image:
      "https://static.readdy.ai/image/ef1aae41220ad17a1705abffff22a58b/5cf4c265d53a873b25b1ce7222cd37ed.png",
    tint: "0.905 0.04 230",
  },
];

export default function Services() {
  const sectionRef = useRef<HTMLElement>(null);
  const leftPanelRef = useRef<HTMLDivElement>(null);

  const imageRefs = useRef<Array<HTMLDivElement | null>>([]);

  const numberRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const kickerRef = useRef<HTMLDivElement>(null);
  const guideRef = useRef<HTMLParagraphElement>(null);
  const progressBarRefs = useRef<Array<HTMLDivElement | null>>([]);

  const lastPaintedRef = useRef({
    activeIdx: -1,
    opacities: [-1, -1, -1] as number[],
    tint: "",
    barFills: [-1, -1, -1] as number[],
  });

  const lerpTint = (a: string, b: string, t: number) => {
    const [al, ac, ah] = a.split(" ").map(Number);
    const [bl, bc, bh] = b.split(" ").map(Number);
    let dh = bh - ah;
    if (dh > 180) dh -= 360;
    if (dh < -180) dh += 360;
    const l = al + (bl - al) * t;
    const c = ac + (bc - ac) * t;
    const h = ah + dh * t;
    return `oklch(${l.toFixed(4)} ${c.toFixed(4)} ${h.toFixed(3)})`;
  };

  const paint = (p: number) => {
    const slidePos = p * (SERVICES.length - 1);
    const activeIdx = Math.min(
      SERVICES.length - 1,
      Math.max(0, Math.round(slidePos)),
    );
    const last = lastPaintedRef.current;

    // 1) Crossfade opacities
    for (let i = 0; i < SERVICES.length; i++) {
      const d = Math.abs(slidePos - i);
      const opacity = Math.max(0, 1 - d);
      if (Math.abs(opacity - last.opacities[i]) > 0.005) {
        const img = imageRefs.current[i];
        if (img) img.style.opacity = opacity.toFixed(3);
        last.opacities[i] = opacity;
      }
    }

    // 2) Panel tint
    const leftPanel = leftPanelRef.current;
    if (leftPanel) {
      const lower = Math.floor(slidePos);
      const upper = Math.min(SERVICES.length - 1, lower + 1);
      const localT = slidePos - lower;
      const tint = lerpTint(SERVICES[lower].tint, SERVICES[upper].tint, localT);
      if (tint !== last.tint) {
        leftPanel.style.backgroundColor = tint;
        last.tint = tint;
      }
    }

    // 3) Active text
    if (activeIdx !== last.activeIdx) {
      const svc = SERVICES[activeIdx];
      if (numberRef.current) numberRef.current.textContent = svc.number;
      if (titleRef.current) titleRef.current.textContent = svc.title;
      if (kickerRef.current) kickerRef.current.textContent = svc.kicker;
      if (guideRef.current) guideRef.current.textContent = svc.guide;
      last.activeIdx = activeIdx;
    }

    // 4) Progress bars
    for (let i = 0; i < SERVICES.length; i++) {
      let fill = 0;
      if (i < slidePos) fill = 1;
      else if (i === Math.floor(slidePos)) fill = slidePos - i;
      const bucket = Math.round(fill * 100);
      if (bucket !== last.barFills[i]) {
        const bar = progressBarRefs.current[i];
        if (bar) bar.style.transform = `scaleX(${(bucket / 100).toFixed(2)})`;
        last.barFills[i] = bucket;
      }
    }
  };

  useSectionScrollProgress(sectionRef, (p, inView) => {
    if (!inView) return;
    paint(p);
  });

  return (
    <section
      ref={sectionRef}
      id="services"
      className="relative w-full bg-background-50"
      style={{
        height: "300vh",
        contain: "layout paint",
        isolation: "isolate",
      }}
    >
      <div
        className="sticky top-0 w-full h-screen overflow-hidden"
        style={{ contain: "layout paint" }}
      >
        {/* Section header */}
        <div className="absolute top-0 left-0 right-0 z-30 px-6 md:px-10 pt-28 md:pt-32 pointer-events-none">
          <div className="max-w-[1600px] mx-auto flex items-baseline justify-between">
            <p className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium">
              Our Services
            </p>
            <p className="text-[10px] tracking-[0.35em] uppercase text-foreground-950/60 font-medium hidden md:block">
              Three ways to begin
            </p>
          </div>
        </div>

        <div className="flex h-full">
          {/* LEFT — image panel */}
          <div
            ref={leftPanelRef}
            className="relative hidden md:block w-1/2 h-full overflow-hidden"
            style={{
              backgroundColor: `oklch(${SERVICES[0].tint})`,
            }}
          >
            {SERVICES.map((svc, i) => (
              <div
                key={svc.id}
                ref={(el) => {
                  imageRefs.current[i] = el;
                }}
                className="absolute inset-0"
                style={{
                  opacity: i === 0 ? 1 : 0,
                  willChange: "opacity",
                  transform: "translateZ(0)",
                  backfaceVisibility: "hidden",
                }}
              >
                <img
                  src={svc.image}
                  alt={`${svc.title} — Serene`}
                  className="w-full h-full object-cover object-center"
                  loading={i === 0 ? "eager" : "lazy"}
                  decoding="async"
                />
              </div>
            ))}

            <div className="absolute bottom-8 left-8 flex items-center gap-3 text-background-50/85 z-10">
              <span className="text-[10px] tracking-[0.3em] uppercase font-medium">
                Slide
              </span>
              <span className="w-8 h-[1px] bg-background-50/60"></span>
              <span className="font-heading text-sm font-medium">
                {SERVICES.length.toString().padStart(2, "0")}
              </span>
            </div>
          </div>

          {/* RIGHT — info column */}
          <div className="relative w-full md:w-1/2 h-full flex items-center px-6 md:px-14 lg:px-20 bg-background-50">
            <div className="w-full max-w-xl">
              <div
                ref={numberRef}
                className="font-heading text-foreground-950/25 leading-none mb-8"
                style={{
                  fontSize: "clamp(4rem, 8vw, 7.5rem)",
                  fontWeight: 300,
                  letterSpacing: "-0.04em",
                  fontVariationSettings: "'opsz' 144",
                }}
              >
                01
              </div>

              <h2
                ref={titleRef}
                className="font-heading text-foreground-950 leading-[1.02] mb-4"
                style={{
                  fontSize: "clamp(2.75rem, 4.6vw, 4.75rem)",
                  fontWeight: 400,
                  letterSpacing: "-0.02em",
                  fontVariationSettings: "'opsz' 144",
                }}
              >
                Individual Therapy
              </h2>

              <div
                ref={kickerRef}
                className="font-heading italic text-foreground-950/70 mb-10"
                style={{
                  fontSize: "clamp(1.25rem, 1.75vw, 1.75rem)",
                  fontWeight: 300,
                  letterSpacing: "-0.005em",
                  fontVariationSettings: "'opsz' 144",
                }}
              >
                One person, one quiet room.
              </div>

              <div className="w-14 h-[1px] bg-foreground-950/25 mb-8"></div>

              <p
                ref={guideRef}
                className="font-sans text-foreground-950/85 leading-[1.65] mb-12 max-w-[440px]"
                style={{ fontSize: "clamp(1rem, 1.15vw, 1.15rem)" }}
              >
                For you, if lately you've been carrying too much on your own.
              </p>

              <div className="flex items-center gap-4">
                {SERVICES.map((svc, i) => (
                  <div key={svc.id} className="flex items-center gap-3">
                    <span className="text-[10px] tracking-[0.28em] uppercase font-medium text-foreground-950/60">
                      {svc.number}
                    </span>
                    <div className="relative w-16 h-[2px] bg-foreground-950/15 overflow-hidden rounded-full">
                      <div
                        ref={(el) => {
                          progressBarRefs.current[i] = el;
                        }}
                        className="absolute inset-0 bg-foreground-950 origin-left"
                        style={{
                          transform: "scaleX(0)",
                          transformOrigin: "left center",
                          willChange: "transform",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link
                  to="/services"
                  className="group inline-flex items-center gap-3 bg-foreground-950 text-background-50 rounded-full pl-8 pr-2 py-2 whitespace-nowrap cursor-pointer transition-transform duration-300 hover:-translate-y-0.5"
                >
                  <span className="text-[11px] tracking-[0.3em] uppercase font-medium">
                    See Services
                  </span>
                  <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform duration-300 group-hover:translate-x-0.5">
                    <i className="ri-arrow-right-up-line text-lg"></i>
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}